import { CheckCircle, XCircle, Award, Star, Briefcase, ShieldCheck, ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { VendorMatchScore } from '@/lib/types/job';
import { MatchScoreTooltip } from './MatchScoreTooltip';

interface MatchScoreDisplayProps {
  matchScore: VendorMatchScore;
  compact?: boolean;
}

export const MatchScoreDisplay = ({ matchScore, compact = false }: MatchScoreDisplayProps) => {
  console.log('[MatchScoreDisplay] Rendering with:', { 
    score: matchScore.score, 
    compact, 
    totalCriteria: matchScore.total_criteria,
    hasDetails: !!matchScore.details,
    details: matchScore.details
  });

  const { total_criteria, score, details, criterion_scores } = matchScore;

  if (details.no_criteria) {
    return compact ? (
      <Badge variant="secondary">No Criteria</Badge>
    ) : (
      <div className="text-sm text-muted-foreground">No match criteria set</div>
    );
  }

  if (details.not_verified) {
    return compact ? (
      <Badge variant="destructive">Not Verified</Badge>
    ) : (
      <div className="text-sm text-destructive">Vendor not verified</div>
    );
  }

  const getScoreColor = () => {
    if (score >= 90) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBadgeVariant = (): "default" | "secondary" | "destructive" | "outline" => {
    if (score >= 90) return 'default';
    if (score >= 60) return 'secondary';
    return 'destructive';
  };

  const getScoreLabel = () => {
    if (score >= 90) return 'Perfect Match';
    if (score >= 60) return 'Good Match';
    return 'Partial Match';
  };

  if (compact) {
    const badgeContent = (
      <Badge 
        variant={getScoreBadgeVariant()} 
        className="cursor-pointer hover:scale-105 hover:shadow-md transition-all duration-200 flex items-center gap-1"
      >
        {score}% Match
        <ChevronDown className="h-3 w-3" />
      </Badge>
    );

    return (
      <div className="w-fit">
        <Popover>
          <PopoverTrigger asChild>
            {badgeContent}
          </PopoverTrigger>
          <PopoverContent className="w-auto p-3" align="start">
            <MatchScoreTooltip matchScore={matchScore} />
          </PopoverContent>
        </Popover>
      </div>
    );
  }

  const criteriaChecks = [];

  // Certifications with granular score
  if (details.certifications_required && details.certifications_required.length > 0) {
    const certsScore = details.certifications_score || 0;
    const matchedCount = details.certifications_matched || 0;
    const requiredCount = details.certifications_required.length;
    
    criteriaChecks.push({
      icon: Award,
      label: 'Certifications',
      score: certsScore,
      met: details.certifications_met || false,
      value: `${matchedCount} of ${requiredCount} required certification${requiredCount !== 1 ? 's' : ''}`
    });
  }

  // Rating with granular score
  if (details.min_rating_required) {
    const ratingScore = details.rating_score || 0;
    criteriaChecks.push({
      icon: Star,
      label: 'Rating',
      score: ratingScore,
      met: details.rating_met || false,
      value: details.vendor_rating 
        ? `${details.vendor_rating.toFixed(1)} stars (minimum ${details.min_rating_required})`
        : 'No rating yet'
    });
  }

  // Completed Jobs with granular score
  if (details.min_jobs_required) {
    const jobsScore = details.jobs_score || 0;
    criteriaChecks.push({
      icon: Briefcase,
      label: 'Experience',
      score: jobsScore,
      met: details.jobs_met || false,
      value: `${details.vendor_completed_jobs || 0} completed jobs (minimum ${details.min_jobs_required})`
    });
  }

  // Verification Level with granular score
  if (details.min_verification_level && details.min_verification_level !== 'basic') {
    const verificationScore = details.verification_score || 0;
    criteriaChecks.push({
      icon: ShieldCheck,
      label: 'Verification',
      score: verificationScore,
      met: details.verification_met || false,
      value: `${details.vendor_verification_level} (requires ${details.min_verification_level})`
    });
  }

  const scoreHeader = (
    <button 
      type="button"
      className="flex items-center gap-1.5 cursor-pointer bg-transparent border-none p-0 hover:opacity-80 transition-all duration-200 group"
      onClick={() => console.log('[MatchScoreDisplay] Score header clicked')}
    >
      <span className={`${getScoreColor()} group-hover:underline`}>
        {getScoreLabel()}
      </span>
      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground group-hover:translate-y-0.5 transition-transform" />
    </button>
  );

  return (
    <Card className={score >= 90 ? 'border-green-200 bg-green-50/50' : ''}>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center justify-between">
          <Popover>
            <PopoverTrigger asChild>
              {scoreHeader}
            </PopoverTrigger>
            <PopoverContent className="w-auto p-3" align="start">
              <MatchScoreTooltip matchScore={matchScore} />
            </PopoverContent>
          </Popover>
          <Badge 
            variant={getScoreBadgeVariant()}
            className="flex items-center gap-1"
          >
            {score}%
            <ChevronDown className="h-3 w-3" />
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {criteriaChecks.map((check, index) => (
          <div key={index} className="space-y-1.5">
            <div className="flex items-start gap-2 text-sm">
              <check.icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${check.met ? 'text-green-600' : 'text-amber-600'}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium truncate">{check.label}</p>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className={`text-xs font-semibold ${check.score >= 90 ? 'text-green-600' : check.score >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                      {Math.round(check.score)}%
                    </span>
                    {check.met ? (
                      <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                    ) : (
                      <XCircle className="h-3.5 w-3.5 text-amber-600" />
                    )}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{check.value}</p>
              </div>
            </div>
            <Progress 
              value={check.score} 
              className="h-1.5"
            />
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
